import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Calendar, Download } from 'lucide-react';
import { usePermissions } from '@contexts/AuthContext';
import { schedulerService } from '@services';
import { useSchedulePageState } from '@hooks/useSchedulePageState';
import { useSchedulePageModals } from '@hooks/useSchedulePageModals';
import { useSchedulePageData } from '@hooks/useSchedulePageData';
import { useSchedulePageHandlers } from '@hooks/useSchedulePageHandlers';
import { useScheduleConflictFilter } from '@hooks/useScheduleConflictFilter';
import PageLayout from '@components/layout/PageLayout';
import Button from '@components/common/Button';
import ListPageHeader from '@components/common/ListPageHeader';
import MultiRoleAssignmentModal from '@components/common/MultiRoleAssignmentModal';
import AssessorAssignmentModal from '@components/common/AssessorAssignmentModal';
import ScheduledDateModal from '@components/common/ScheduledDateModal';
import ConfirmDialog from '@components/common/ConfirmDialog';
import { FilterButtons } from '@components/common/FilterButtons';
import AddToGroupModal from '@components/common/AddToGroupModal';
import { ColumnSelector } from '@components/common/table';
import { SchedulerTable, GroupContractsModal, CombineButtonContainer } from '@components/features/scheduler';
import { LoadingState, LoadingOverlay } from '@components/common/loading';
import ErrorState from '@components/common/ErrorState';
import { ScheduleItem, TableColumn } from '@app-types';
import { STATUS_LABELS } from '@utils/statusUtils';
import { exportScheduleItems } from '@utils/exportUtils';
import { getApiErrorMessage } from '@utils/apiError';
import { showToast } from '@utils/toast';
import styles from './SchedulerDashboardPage.module.css';

const contractColumns: TableColumn[] = [
  { key: 'state', label: 'Status', defaultVisible: true, alwaysVisible: true, filterKey: 'state' },
  { key: 'urgency', label: 'Urgency', defaultVisible: true, filterable: false },
  {
    key: 'contract_number',
    label: 'Contract Number',
    defaultVisible: true,
    alwaysVisible: true,
    filterKey: 'contract_number'
  },
  { key: 'name', label: 'Contract Name', defaultVisible: false, sortable: false, filterable: false },
  { key: 'start_date', label: 'Contract Start Date', defaultVisible: false, sortable: false, filterable: false },
  { key: 'end_date', label: 'Contract End Date', defaultVisible: false, sortable: false, filterable: false },
  { key: 'amount', label: 'Contract Amount', defaultVisible: false, sortable: false, filterable: false },
  { key: 'contract_type', label: 'Contract Type', defaultVisible: false, sortable: false, filterable: false },
  { key: 'supplier', label: 'Supplier', defaultVisible: true, filterKey: 'supplier_name' },
  { key: 'tier', label: 'Tier', width: '80px', defaultVisible: true, filterKey: 'tier' },
  { key: 'assessor', label: 'Assessor Name', defaultVisible: true, filterKey: 'assessor_name' },
  { key: 'contract_manager', label: 'Contract Manager(s)', defaultVisible: true, filterKey: 'contract_manager' },
  { key: 'contract_owner', label: 'Contract Owner(s)', defaultVisible: true, filterKey: 'contract_owner' },
  { key: 'meeting_date', label: 'Meeting Date', defaultVisible: true, filterable: false },
  { key: 'division', label: 'Division', defaultVisible: true, filterKey: 'division' },
  { key: 'cost_center', label: 'Cost Center', defaultVisible: false, sortable: false, filterable: false },
  { key: 'target_date', label: 'Target Assessment Date', defaultVisible: true, filterable: false },
  { key: 'in_progress', label: 'Scheduled By', defaultVisible: true, filterKey: 'processed_by' },
  {
    key: 'scheduling_notes',
    label: 'Scheduling Notes',
    defaultVisible: false,
    sortable: false,
    filterable: false
  },
  {
    key: 'rescheduled_date',
    label: 'Rescheduled Meeting Date',
    defaultVisible: false,
    sortable: false,
    filterable: false
  },
  {
    key: 'rescheduled_reason',
    label: 'Rescheduled Reason',
    defaultVisible: false,
    sortable: false,
    filterable: false
  },
  { key: 'view', label: '', defaultVisible: true, sortable: false, hiddenFromSelector: true, filterable: false }
];

// Column to field mapping for sorting
const columnSortMapping: { [key: string]: string } = {
  contract_number: 'contract_number',
  name: 'name',
  supplier: 'supplier_name',
  tier: 'tier',
  division: 'division',
  status: 'meeting_status',
  manager: 'contract_manager',
  contract_owner: 'contract_owner',
  contract_manager: 'contract_manager',
  assessor: 'assessor_name',
  target_date: 'target_date',
  meeting_date: 'meeting_date',
  state: 'state',
  start_date: 'start_date',
  end_date: 'end_date',
  created_at: 'created_at',
  updated_at: 'updated_at'
};

const SchedulerDashboardPage: React.FC = () => {
  const permissions = usePermissions();
  const navigate = useNavigate();

  // Use generic hooks for page management
  const {
    searchQuery,
    debouncedSearchQuery,
    handleSearch,
    filter: stateFilter,
    handleFilterChange,
    checkboxFilter: processedByMe,
    handleCheckboxChange: setProcessedByMe,
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    sortBy,
    sortOrder,
    handleColumnSort,
    getSortIndicator,
    isColumnSorted,
    columns: allColumns,
    visibleColumns,
    renderedColumns,
    toggleColumn,
    // Column filters
    columnFilterValues,
    debouncedColumnFilters,
    setColumnFilter,
    clearColumnFilters,
    hasActiveColumnFilters,
    getColumnFiltersParam
  } = useSchedulePageState({
    defaultFilter: 'pending_data_enrichment',
    defaultSort: 'created_at',
    defaultSortOrder: 'DESC',
    columnSortMapping,
    columns: contractColumns,
    columnStorageKey: 'scheduler-visible-columns'
  });

  // Use generic hook for modals
  const {
    showScheduledDateModal,
    showAssessorModal,
    showManagerModal,
    showOwnerModal,
    showAddToGroupModal,
    showRemoveConfirm,
    selectedItem,
    selectedContractId,
    selectedIsGroupOperation,
    groupToAddTo,
    removeGroupId,
    removeContractId,
    openScheduledDateModal,
    closeScheduledDateModal,
    openAssessorModal,
    closeAssessorModal,
    openManagerModal,
    closeManagerModal,
    openOwnerModal,
    closeOwnerModal,
    openAddToGroupModal,
    closeAddToGroupModal,
    openRemoveConfirm,
    closeRemoveConfirm
  } = useSchedulePageModals();

  // Local state for grouping (not shared between pages)
  const [showGroupContractsModal, setShowGroupContractsModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [selectedItemsData, setSelectedItemsData] = useState<Map<number, ScheduleItem>>(new Map());

  // Auto-deselect items that cannot be selected (pending_data_enrichment, scheduled, or rescheduled)
  useEffect(() => {
    const invalidIds = Array.from(selectedItemsData.entries())
      .filter(([_, item]) => {
        // Cannot select pending_data_enrichment items
        if (stateFilter === 'pending_data_enrichment') return true;
        // Cannot select scheduled or rescheduled meetings
        if (item.meeting?.status === 'scheduled' || item.meeting?.status === 'rescheduled') return true;
        return false;
      })
      .map(([id]) => id);

    if (invalidIds.length > 0) {
      setSelectedItems((prev) => {
        const newSet = new Set(prev);
        invalidIds.forEach((id) => newSet.delete(id));
        return newSet;
      });
      setSelectedItemsData((prev) => {
        const newMap = new Map(prev);
        invalidIds.forEach((id) => newMap.delete(id));
        return newMap;
      });
    }
  }, [selectedItemsData, stateFilter]);

  // Filter columns based on current state filter
  const filteredColumns = useMemo(() => {
    if (stateFilter === 'pending_data_enrichment') {
      // Hide 'assessor', 'meeting_date', 'in_progress', and 'view' columns for pending_data_enrichment
      return renderedColumns.filter(
        (col) => col.key !== 'assessor' && col.key !== 'meeting_date' && col.key !== 'in_progress' && col.key !== 'view'
      );
    }
    if (stateFilter === 'ready_for_scheduling') {
      // Hide 'view' column for ready_for_scheduling (view only available on scheduled tab)
      return renderedColumns.filter((col) => col.key !== 'view');
    }
    return renderedColumns;
  }, [stateFilter, renderedColumns]);

  // Use generic hook for data fetching
  const { scheduleItems, isLoading, error, isFetching, filterCounts, totalItems, totalPages, refreshData } =
    useSchedulePageData({
      queryKey: [
        'schedule-items',
        debouncedSearchQuery,
        stateFilter,
        processedByMe,
        sortBy,
        sortOrder,
        currentPage,
        pageSize,
        debouncedColumnFilters
      ],
      pageSize, // Pass pageSize for correct totalPages calculation
      queryFn: () => {
        const filters = {
          state: stateFilter,
          processedByMe: processedByMe || undefined,
          searchTerm: debouncedSearchQuery || undefined,
          searchColumns: visibleColumns.size > 0 ? Array.from(visibleColumns) : contractColumns.map((col) => col.key),
          columnFilters: getColumnFiltersParam() || undefined,
          sortBy: sortBy,
          order: sortOrder as 'ASC' | 'DESC',
          limit: pageSize,
          offset: (currentPage - 1) * pageSize,
          viewMode: 'scheduler' as const
        };
        return schedulerService.getScheduleItems(filters);
      }
    });

  // Get selected schedule items from stored data (supports multi-page selection)
  const selectedScheduleItems = useMemo(() => {
    return Array.from(selectedItemsData.values());
  }, [selectedItemsData]);

  // Use hook for conflict filter button management
  const { stateFilters: conflictStateFilters } = useScheduleConflictFilter({
    filterCounts,
    isLoading,
    currentFilter: stateFilter,
    initialFilter: 'pending_data_enrichment', // Default filter for Scheduler page
    autoSwitchTargetFilter: 'ready_for_scheduling', // Target when conflicts resolved
    handleFilterChange,
    debouncedSearchQuery,
    hasActiveColumnFilters,
    baseFilters: [
      { key: 'pending_data_enrichment', label: STATUS_LABELS['pending_data_enrichment'], type: 'warning' as const },
      { key: 'ready_for_scheduling', label: STATUS_LABELS['ready_for_scheduling'], type: 'primary' as const },
      { key: 'scheduled', label: STATUS_LABELS['scheduled'], type: 'primary' as const }
    ],
    hasOtherActiveFilters: processedByMe // processedByMe=false is default for Scheduler page
  });

  // Add dynamic "Reassessment Due" tab (cross-cutting, like Conflicts)
  const stateFilters = useMemo(() => {
    const filters = [...conflictStateFilters];
    const reassessmentDueTotal = filterCounts.reassessmentDueTotal ?? filterCounts.reassessment_due ?? 0;

    if (reassessmentDueTotal > 0) {
      const conflictsIndex = filters.findIndex(f => f.key === 'conflicts');
      const insertAt = conflictsIndex >= 0 ? conflictsIndex + 1 : 0;
      filters.splice(insertAt, 0, {
        key: 'reassessment_due',
        label: STATUS_LABELS['reassessment_due'],
        type: 'warning' as const,
        tooltip: 'Contracts due for reassessment — also counted in their status tab'
      });
    }

    return filters;
  }, [conflictStateFilters, filterCounts]);

  // Auto-switch away from reassessment_due tab when count drops to 0
  useEffect(() => {
    const reassessmentDueTotal = filterCounts.reassessmentDueTotal ?? filterCounts.reassessment_due ?? 0;
    if (!isLoading && stateFilter === 'reassessment_due' && filterCounts.reassessment_due === 0 && reassessmentDueTotal === 0) {
      handleFilterChange('ready_for_scheduling');
    }
  }, [filterCounts, isLoading, stateFilter, handleFilterChange]);

  // Use generic hook for handlers
  const {
    handleAssignRole,
    handleScheduleDate,
    handleDateSelected,
    handleAssessorAssigned,
    handleManagerAssigned,
    handleOwnerAssigned,
    handleAddToGroup,
    handleAddContractsToGroup,
    handleRemoveFromGroup,
    confirmRemoveFromGroup
  } = useSchedulePageHandlers({
    selectedItem,
    selectedContractId,
    selectedIsGroupOperation,
    removeGroupId,
    removeContractId,
    openScheduledDateModal,
    closeScheduledDateModal,
    openAssessorModal,
    closeAssessorModal,
    openManagerModal,
    closeManagerModal,
    openOwnerModal,
    closeOwnerModal,
    openAddToGroupModal,
    closeAddToGroupModal,
    openRemoveConfirm,
    closeRemoveConfirm,
    refreshData,
    apiMethods: {
      updateOwners: schedulerService.updateContractOwners.bind(schedulerService),
      updateManagers: schedulerService.updateContractManagers.bind(schedulerService),
      updateAssessor: async (item: ScheduleItem, assessorId: number) => {
        const contractId = item.contracts[0]?.id;
        if (item.id !== null || contractId) {
          await schedulerService.updateScheduleItemAssessor(item.id, assessorId, contractId);
        }
      },
      scheduleOrReschedule: async (item: ScheduleItem, date: string, reason?: string, notes?: string) => {
        if (date) {
          await schedulerService.scheduleScheduleItemMeeting(item.id, item.contracts[0].id, date, reason, notes);
        } else {
          await schedulerService.deleteScheduleItemMeeting(item.id, item.contracts[0].id, reason, notes);
        }
      },
      addToGroup: schedulerService.addContractsToGroup.bind(schedulerService),
      removeFromGroup: schedulerService.removeContractFromGroup.bind(schedulerService)
    },
    onAddToGroupSuccess: () => {
      setSelectedItems(new Set());
      setSelectedItemsData(new Map());
    },
    onRemoveFromGroupSuccess: () => {
      setSelectedItems(new Set());
      setSelectedItemsData(new Map());
    }
  });

  // Selection handlers
  const handleToggleSelection = useCallback((itemId: number | null, contractId: number, item?: ScheduleItem) => {
    // Prevent selection of scheduled or rescheduled meetings
    if (item?.meeting?.status === 'scheduled' || item?.meeting?.status === 'rescheduled') {
      return;
    }

    const selectionId = itemId !== null ? itemId : contractId;

    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(selectionId)) {
        newSet.delete(selectionId);
      } else {
        newSet.add(selectionId);
      }
      return newSet;
    });

    // Store or remove the full item data
    if (item) {
      setSelectedItemsData((prev) => {
        const newMap = new Map(prev);
        if (newMap.has(selectionId)) {
          newMap.delete(selectionId);
        } else {
          newMap.set(selectionId, item);
        }
        return newMap;
      });
    }
  }, []);

  const canScheduleMeeting = useCallback(
    (item: ScheduleItem) => {
      if (!permissions.canAccessScheduler) return false;
      if (!item.assessor || item.assessor.id <= 0) return false;
      return item.contracts.every((c) => c.contract_managers.length > 0);
    },
    [permissions.canAccessScheduler]
  );

  // Grouping handlers
  const handleGroupContracts = async (contractIds: number[], assessorId: number | null) => {
    await schedulerService.groupContracts({
      contract_ids: contractIds,
      assessor_id: assessorId || 0 // 0 means no assessor assigned yet
    });

    // Clear selection after successful grouping
    setSelectedItems(new Set());
    setSelectedItemsData(new Map());
    setShowGroupContractsModal(false);

    // Refresh data
    refreshData();

    // Scroll to top of the table after a short delay to ensure content is rendered
    setTimeout(() => {
      const tableElement = document.querySelector('.scheduler-table');
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Function to deselect all items
  const handleDeselectAll = useCallback(() => {
    setSelectedItems(new Set());
    setSelectedItemsData(new Map());
  }, []);

  // Export mutation - fetches the full filtered dataset (all pages), not just the
  // current page. Uses `filteredColumns` (not raw `visibleColumns`) so the export
  // matches what's actually shown on the current status tab — some columns (e.g.
  // assessor/meeting_date) are hidden on certain tabs regardless of the user's
  // ColumnSelector picks.
  const exportMutation = useMutation({
    mutationFn: () =>
      schedulerService.getScheduleItemsForExport({
        state: stateFilter,
        processedByMe: processedByMe || undefined,
        searchTerm: debouncedSearchQuery || undefined,
        searchColumns: visibleColumns.size > 0 ? Array.from(visibleColumns) : contractColumns.map((col) => col.key),
        columnFilters: getColumnFiltersParam() || undefined,
        sortBy: sortBy,
        order: sortOrder as 'ASC' | 'DESC',
        viewMode: 'scheduler'
      }),
    onSuccess: (response) => {
      exportScheduleItems(
        response.data,
        'scheduler',
        filteredColumns.map((col) => col.key),
        'contract-meetings'
      );
    },
    onError: (err: unknown) => {
      console.error('Failed to export schedule items:', err);
      showToast.error(getApiErrorMessage(err, 'Failed to export schedule items. Please try again.'));
    }
  });

  if (error) {
    return (
      <PageLayout>
        <ErrorState message="Error loading schedule items. Please try again." />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="contracts-page">
        <ListPageHeader
          icon={<Calendar size={24} />}
          title="Schedule Meetings"
          count={filterCounts.all}
          searchValue={searchQuery}
          onSearchChange={handleSearch}
          searchPlaceholder="Search contracts, suppliers..."
          actions={
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => exportMutation.mutate()}
                disabled={exportMutation.isPending || !scheduleItems || scheduleItems.length === 0}
              >
                <Download size={16} />
                {exportMutation.isPending ? 'Exporting...' : 'Export List'}
              </Button>
              <ColumnSelector
                columns={allColumns}
                visibleColumns={Array.from(visibleColumns)}
                onToggleColumn={toggleColumn}
              />
            </>
          }
          additionalContent={
            selectedItems.size >= 2 && stateFilter !== 'pending_data_enrichment' ? (
              <CombineButtonContainer
                selectedCount={selectedItems.size}
                onCombine={() => setShowGroupContractsModal(true)}
                onDeselectAll={handleDeselectAll}
              />
            ) : undefined
          }
        />

        <FilterButtons
          filters={stateFilters}
          activeFilter={stateFilter}
          onFilterChange={handleFilterChange}
          filterCounts={filterCounts}
          showCheckbox={true}
          checkboxLabel="Show only meetings processed by me"
          checkboxChecked={processedByMe}
          onCheckboxChange={setProcessedByMe}
          dataTestId="scheduler-filter"
        />

        {/* Show spinner overlay when fetching data (filter changes) */}
        {isFetching && !isLoading && <LoadingOverlay variant="page" />}

        {isLoading ? (
          <LoadingState message="Loading schedule items..." />
        ) : (
          <>
            {/* Always show table with filters so user can clear them even when no results */}
            <div className={styles.schedulerTable}>
              <SchedulerTable
                scheduleItems={scheduleItems}
                columns={filteredColumns}
                permissions={permissions}
                selectedItems={selectedItems}
                onToggleSelection={handleToggleSelection}
                onAssignRole={handleAssignRole}
                onScheduleDate={handleScheduleDate}
                onRemoveFromGroup={handleRemoveFromGroup}
                onAddToGroup={handleAddToGroup}
                canScheduleMeeting={canScheduleMeeting}
                navigate={navigate}
                onColumnSort={handleColumnSort}
                sortBy={sortBy}
                sortOrder={sortOrder}
                getSortIndicator={getSortIndicator}
                isColumnSorted={isColumnSorted}
                currentStateFilter={stateFilter}
                pagination={{
                  currentPage,
                  totalPages,
                  totalItems,
                  pageSize,
                  onPageChange: setCurrentPage,
                  onPageSizeChange: setPageSize,
                  itemName: 'items',
                  pageSizeOptions: [25, 50, 100]
                }}
                columnFilters={{
                  values: columnFilterValues,
                  onChange: setColumnFilter,
                  onClearAll: clearColumnFilters,
                  hasActiveFilters: hasActiveColumnFilters
                }}
                isEmpty={scheduleItems.length === 0}
              />
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {showOwnerModal &&
        selectedItem &&
        selectedContractId &&
        (() => {
          const contract = selectedItem.contracts.find((c) => c.id === selectedContractId);
          return contract ? (
            <MultiRoleAssignmentModal
              contractNumber={contract.contract_number}
              supplierName={contract.supplier_name}
              roleType="owner"
              currentAssigneeIds={contract.contract_owners?.map((o) => o.id) || []}
              division={contract.division}
              onClose={closeOwnerModal}
              onAssigned={handleOwnerAssigned}
            />
          ) : null;
        })()}

      {showManagerModal &&
        selectedItem &&
        selectedContractId &&
        (() => {
          const contract = selectedItem.contracts.find((c) => c.id === selectedContractId);
          return contract ? (
            <MultiRoleAssignmentModal
              contractNumber={contract.contract_number}
              supplierName={contract.supplier_name}
              roleType="manager"
              currentAssigneeIds={contract.contract_managers?.map((m) => m.id) || []}
              onClose={closeManagerModal}
              onAssigned={handleManagerAssigned}
            />
          ) : null;
        })()}

      {showAssessorModal && selectedItem && (
        <AssessorAssignmentModal
          scheduleItem={selectedItem}
          onClose={closeAssessorModal}
          onAssigned={handleAssessorAssigned}
        />
      )}

      {showGroupContractsModal && (
        <GroupContractsModal
          selectedContracts={selectedScheduleItems}
          onClose={() => setShowGroupContractsModal(false)}
          onGroup={handleGroupContracts}
        />
      )}

      {showAddToGroupModal && groupToAddTo && (
        <AddToGroupModal
          title="Add Contracts to Group"
          groupMetadata={{
            tier: groupToAddTo.contracts[0]?.tier || 'N/A',
            assessor: groupToAddTo.assessor?.name,
            status: groupToAddTo.contracts[0]?.state || 'N/A',
            count: groupToAddTo.contracts.length
          }}
          groupId={groupToAddTo.id!}
          queryKey={[
            'compatible-contracts',
            groupToAddTo.id?.toString() || '',
            groupToAddTo.contracts[0]?.state || '',
            groupToAddTo.assessor?.id?.toString() || ''
          ]}
          queryFn={() =>
            schedulerService.getCompatibleContracts({
              state: groupToAddTo.contracts[0]?.state,
              assessorId: groupToAddTo.assessor?.id,
              managerIds: groupToAddTo.contracts[0]?.contract_managers?.map((m) => m.id) || [],
              groupId: groupToAddTo.id
            })
          }
          onClose={closeAddToGroupModal}
          onAdd={handleAddContractsToGroup}
        />
      )}

      {showScheduledDateModal && selectedItem && (
        <ScheduledDateModal
          contract={selectedItem.contracts[0]}
          scheduleItemId={selectedItem.id}
          currentDate={selectedItem.meeting?.scheduled_datetime}
          onClose={closeScheduledDateModal}
          onDateSelected={handleDateSelected}
        />
      )}

      {showRemoveConfirm && (
        <ConfirmDialog
          isOpen={showRemoveConfirm}
          title="Remove Contract from Group"
          message="Are you sure you want to remove this contract from the group?"
          confirmText="Remove"
          cancelText="Cancel"
          confirmVariant="danger"
          onConfirm={confirmRemoveFromGroup}
          onCancel={closeRemoveConfirm}
        />
      )}
    </PageLayout>
  );
};

export default SchedulerDashboardPage;
